import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import tokens from '@lacedltd/design-tokens'
import {
  generateBorder,
  mediaQuery,
  Typography
} from '@lacedltd/laced-components'
import { SaleItemSubColors } from './ProductSaleItem.types'

const collapseOut = keyframes({
  '0%': { opacity: 1, maxHeight: '200px', transform: 'scale(1)' },
  '40%': { opacity: 0.6, transform: 'scale(0.98)' },
  '100%': {
    opacity: 0,
    maxHeight: 0,
    padding: 0,
    margin: 0,
    border: 'none',
    transform: 'scale(0.95)',
    overflow: 'hidden'
  }
})

const colorMap: Record<SaleItemSubColors, string> = {
  accent: tokens.color.airJordan[300],
  danger: tokens.color.roshe[600],
  info: tokens.color.foamRunner[600],
  default: tokens.color.airJordan[400]
}

export const SaleItemWrap = styled.div({
  display: 'flex',
  gap: tokens.spacing[4],
  flexDirection: 'row',
  position: 'relative',
  marginBottom: 0,
  padding: `${tokens.spacing[4]} ${tokens.spacing[5]}`,
  border: `${tokens.borderWidth.xs} solid ${tokens.color.airJordan[200]}`,
  borderRadius: tokens.borderRadius.s,
  backgroundColor: tokens.color.yeezy[0],
  transition: 'border-color 0.15s ease',

  '&:hover': {
    borderColor: tokens.color.airJordan[300]
  },

  '> img': {
    width: '2.5rem',
    height: '2.5rem',
    objectFit: 'contain',
    alignSelf: 'center',
    flexShrink: 0,
    borderRadius: tokens.borderRadius.s
  },

  [mediaQuery.m.min]: {
    '> img': {
      width: '3rem',
      height: '3rem',
      alignSelf: 'center'
    }
  }
})

export const SaleItemInnerWrap = styled.div({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: tokens.spacing[3],
  width: '100%',
  minWidth: 0,

  // On mobile stack the columns (info → price/breakdown → any warning/actions)
  // so a long payout breakdown or "stale listing" note never squeezes the
  // title/size column to nothing. The ⋮ actions menu is absolutely positioned
  // (top-right of the card) so it stays out of this flow.
  [mediaQuery.m.max]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: tokens.spacing[2]
  },

  [mediaQuery.m.min]: {
    gap: tokens.spacing[4]
  }
})

export const SaleItemInfoWrap = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  gap: tokens.spacing[1],
  paddingRight: tokens.spacing[4],
  minWidth: 0,
  flex: '1 1 0%',
  overflowX: 'hidden',

  // reserve room on the first row for the absolute ⋮ actions button
  [mediaQuery.m.max]: {
    paddingRight: '2rem'
  },

  [mediaQuery.m.min]: {
    paddingRight: tokens.spacing[4]
  }
})

export const SaleItemHeading = styled.a({
  fontSize: tokens.fontSize[2],
  fontWeight: 500,
  color: tokens.color.airJordan.p,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: 'block',

  '&:hover': {
    color: tokens.color.airJordan[600]
  }
})

export const SaleItemSub = styled.span<{
  type?: SaleItemSubColors
}>(
  {
    fontSize: tokens.fontSize[1],
    lineHeight: 1.4
  },
  ({ type = 'default' }) => ({
    color: type === 'default' ? tokens.color.airJordan[400] : colorMap[type]
  })
)

export const SaleItemSubRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: tokens.spacing[5],
  flexWrap: 'wrap'
})

// A hair of left + bottom margin so the PremiumLabel's border isn't shaved off
// by an overflow-hidden ancestor when the badge sits flush against the edge.
export const PremiumLabelWrap = styled.span({
  marginLeft: tokens.spacing[1],
  marginBottom: tokens.spacing[1]
})

export const SaleItemPriceWrap = styled.div<{ center?: boolean }>(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ({ center: _center }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacing[1],
    justifyContent: 'center',
    alignItems: 'flex-end',
    flexShrink: 0,
    marginLeft: 'auto',
    textAlign: 'right',
    color: tokens.color.airJordan.p,

    a: {
      marginTop: tokens.spacing[1]
    },

    // On mobile the inner wrap stacks, so this column goes full-width and
    // left-aligns under the title (price on its row, breakdown below it).
    [mediaQuery.m.max]: {
      width: '100%',
      maxWidth: 'none',
      marginLeft: 0,
      alignItems: 'flex-start',
      textAlign: 'left'
    }
  })
)

export const SaleItemPrice = styled.span<{ strikethrough?: boolean }>(
  ({ strikethrough }) => ({
    fontSize: tokens.fontSize[2],
    fontWeight: 500,
    color: tokens.color.airJordan[500],
    textDecoration: strikethrough ? 'line-through' : 'none'
  })
)

export const SaleItemPriceRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: tokens.spacing[2],

  [mediaQuery.m.max]: {
    gap: tokens.spacing[1]
  }
})

// Compact one-line price formula rendered under the SaleItemPrice
// (e.g. POIZON `£200 + £25 ship + £36 customs = £261` on admin
// surfaces, seller `→ £160 payout` on /account/selling). Lives
// inside SaleItemPriceWrap so it inherits the right-aligned column
// and small accent colour, but kept tight and monospaced so the
// numbers line up visually with the price above.
export const SaleItemPriceBreakdown = styled.span({
  fontSize: tokens.fontSize[1],
  lineHeight: 1.4,
  color: tokens.color.airJordan[500],
  fontFamily:
    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  whiteSpace: 'nowrap',
  letterSpacing: '-0.01em',

  // full-width + left-aligned on mobile now that the columns stack
  [mediaQuery.m.max]: {
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
    textAlign: 'left'
  }
})

export const SaleItemPriceActionsWrap = styled.div<{
  showActions?: boolean
  hasRetrievalAction?: boolean
}>(
  ({ showActions, hasRetrievalAction }) => ({
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    justifyContent: 'flex-end',
    flexShrink: 0,
    gap: hasRetrievalAction ? tokens.spacing[2] : 0,

    ...(hasRetrievalAction
      ? {}
      : {
          a: {
            minHeight: 'auto',
            whiteSpace: 'nowrap',
            fontSize: tokens.fontSize[1],
            padding: `${tokens.spacing[1]} ${tokens.spacing[3]}`,
            border: `${tokens.borderWidth.xs} solid ${tokens.color.airJordan[200]}`,
            color: tokens.color.airJordan[500],
            background: 'transparent',
            borderRadius: tokens.borderRadius.s,
            lineHeight: 1.4,
            transition: 'all 0.15s ease',

            '&:hover': {
              borderColor: tokens.color.airJordan[400],
              color: tokens.color.airJordan.p
            },

            '&[disabled]': {
              color: tokens.color.airJordan[300],
              borderColor: tokens.color.airJordan[100],
              pointerEvents: 'none',
              cursor: 'default'
            }
          },

          div: {
            flexDirection: 'column',
            gap: tokens.spacing[1],
            justifyContent: 'center',
            alignItems: 'flex-end',
            display: 'inline-flex',

            'a, button': {
              textAlign: 'center',
              width: '100%',
              fontSize: `${tokens.fontSize[1]} !important`,
              padding: `${tokens.spacing[1]} ${tokens.spacing[3]} !important`,
              lineHeight: '1.4 !important',
              minHeight: 'auto !important',
              boxSizing: 'border-box'
            }
          }
        }),

    [mediaQuery.m.max]: hasRetrievalAction
      ? {
          position: 'static',
          width: '100%',
          marginTop: tokens.spacing[2],
          justifyContent: 'flex-start'
        }
      : {
          position: 'absolute',
          top: tokens.spacing[2],
          right: tokens.spacing[2],

          div: {
            display: showActions ? 'flex' : 'none',
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: tokens.spacing[1],
            padding: tokens.spacing[2],
            background: tokens.color.yeezy[0],
            borderRadius: tokens.borderRadius.s,
            boxShadow: `0 2px 8px rgba(0, 0, 0, 0.15)`,
            zIndex: 10,
            alignItems: 'stretch',
            width: 'max-content'
          },

          'a, button': {
            textAlign: 'center',
            display: 'block',
            width: '100%',
            fontSize: `${tokens.fontSize[1]} !important`,
            padding: `${tokens.spacing[1]} ${tokens.spacing[3]} !important`,
            lineHeight: '1.4 !important',
            minHeight: 'auto !important',
            boxSizing: 'border-box'
          }
        }
  })
)

export const EllipsisButton = styled.button({
  display: 'none',
  alignItems: 'center',
  justifyContent: 'center',
  width: '1.5rem',
  height: '1.5rem',
  padding: 0,
  border: 'none',
  background: 'transparent',
  color: tokens.color.airJordan[400],
  cursor: 'pointer',
  transition: 'color 0.15s ease',

  '&:hover, &:active': {
    color: tokens.color.airJordan.p
  },

  [mediaQuery.m.max]: {
    display: 'flex'
  }
})

export const MultipleBarcodes = styled.button({
  textDecoration: 'underline',
  cursor: 'pointer',
  marginTop: tokens.spacing[2]
})

export const BarcodeHeading = styled(Typography.Heading)({
  fontSize: tokens.fontSize[3],
  marginBottom: tokens.spacing[2]
})

export const BarcodeSubHeading = styled(Typography.Text)({
  fontSize: tokens.fontSize[1]
})

export const BarcodeList = styled.div({
  marginTop: tokens.spacing[6],
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gridAutoRows: '2.5rem',
  maxHeight: '20rem',
  overflowY: 'auto'
})

export const Barcode = styled(Typography.Text)({
  fontSize: tokens.fontSize[1],
  textAlign: 'center',
  borderBottom: generateBorder('xs', 'solid', 'airJordan.100'),
  marginBottom: tokens.spacing[4],
  paddingBottom: tokens.spacing[4]
})

export const MobileDeleteButton = styled.button({
  display: 'none',
  alignItems: 'center',
  justifyContent: 'center',
  width: '1.5rem',
  height: '1.5rem',
  padding: 0,
  border: 'none',
  background: 'transparent',
  color: tokens.color.airJordan[300],
  cursor: 'pointer',
  transition: 'color 0.15s ease',

  '&:hover, &:active': {
    color: tokens.color.airJordan[500]
  },

  [mediaQuery.m.max]: {
    display: 'flex'
  }
})

export const SaveSuccessOverlay = styled.div<{ visible?: boolean }>(
  ({ visible }) => ({
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing[2],
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    borderRadius: tokens.borderRadius.s,
    fontSize: tokens.fontSize[2],
    fontWeight: 500,
    color: tokens.color.airForce[600],
    opacity: visible ? 1 : 0,
    transition: 'opacity 0.2s ease',
    pointerEvents: 'none',
    zIndex: 1
  })
)

export const ItemCollapse = styled.div<{ collapsing?: boolean }>(
  ({ collapsing }) => ({
    ...(collapsing
      ? {
          animation: `${collapseOut} 0.4s ease forwards`
        }
      : {}),

    // Restyle Badge pills into subtle dot + text status indicators
    '& [data-variant]': {
      background: 'transparent !important',
      border: 'none !important',
      borderRadius: '0 !important',
      padding: '0 !important',
      height: 'auto !important',
      lineHeight: 'normal !important',
      fontSize: '0.6875rem !important',
      fontWeight: '400 !important',
      color: `${tokens.color.airJordan[400]} !important`,
      display: 'inline-flex !important',
      alignItems: 'center',
      gap: '4px',
      letterSpacing: '0.02em',

      '&::before': {
        content: '""',
        display: 'inline-block',
        width: '5px',
        height: '5px',
        borderRadius: '50% !important',
        flexShrink: 0,
        backgroundColor: tokens.color.airJordan[300]
      }
    },
    '& [data-variant="orange"]::before': {
      backgroundColor: `${tokens.color.roshe[500]} !important`
    },
    '& [data-variant="green"]::before': {
      backgroundColor: `${tokens.color.airForce[500]} !important`
    },
    '& [data-variant="blue"]::before': {
      backgroundColor: `${tokens.color.foamRunner[500]} !important`
    },
    '& [data-variant="red"]::before': {
      backgroundColor: `${tokens.color.roshe[600]} !important`
    }
  })
)

export const PriceMatchButton = styled.a({
  display: 'inline-flex',
  alignItems: 'center',
  gap: tokens.spacing[1],
  fontSize: tokens.fontSize[1],
  fontWeight: 400,
  color: tokens.color.airForce[600],
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  textDecoration: 'none',
  lineHeight: 1.4,
  transition: 'color 0.15s ease',
  whiteSpace: 'nowrap',

  '&:hover': {
    color: tokens.color.airForce[700]
  },

  '&[data-updating]': {
    color: tokens.color.airJordan[300],
    pointerEvents: 'none',
    cursor: 'default'
  },

  '&::before': {
    content: '"↓"',
    fontSize: '0.625rem'
  }
})

export const CheckCircle = styled.span({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '1.25rem',
  height: '1.25rem',
  borderRadius: '50%',
  backgroundColor: tokens.color.airForce[100],
  color: tokens.color.airForce[600]
})
